import { html, render, type TemplateResult } from '@design.estate/dees-element';
import type {
  IViewDefinition,
  IViewActivationContext,
  IViewLifecycle,
  TDeesAppui
} from '../../interfaces/appconfig.js';

/**
 * Registry for managing views and their lifecycle
 *
 * Key features:
 * - View caching with hide/show pattern (not destroy/create)
 * - Async content loading support (lazy loading)
 * - View lifecycle hooks (onActivate, onDeactivate, canDeactivate)
 */
export class ViewRegistry {
  private views: Map<string, IViewDefinition> = new Map();
  private instances: Map<string, HTMLElement> = new Map();
  private currentViewId: string | null = null;
  private currentContext?: { params?: Record<string, string>; query?: Record<string, string> };
  private appui: TDeesAppui | null = null;

  /**
   * Set the appui reference for view activation context
   */
  public setAppuiRef(appui: TDeesAppui): void {
    this.appui = appui;
  }

  /**
   * Register a single view
   */
  public register(view: IViewDefinition): void {
    if (this.views.has(view.id)) {
      console.warn(`View with id "${view.id}" already registered. Overwriting.`);
    }
    this.views.set(view.id, view);
  }

  /**
   * Register multiple views
   */
  public registerAll(views: IViewDefinition[]): void {
    views.forEach((view) => this.register(view));
  }

  /**
   * Get a view definition by ID
   */
  public get(viewId: string): IViewDefinition | undefined {
    return this.views.get(viewId);
  }

  /**
   * Get all registered view IDs
   */
  public getViewIds(): string[] {
    return Array.from(this.views.keys());
  }

  /**
   * Get all views
   */
  public getAll(): IViewDefinition[] {
    return Array.from(this.views.values());
  }

  /**
   * Get route for a view
   */
  public getRoute(viewId: string): string {
    const view = this.views.get(viewId);
    return view?.route || view?.id || '';
  }

  /** Build an encoded hash route, omitting absent optional parameters. */
  public buildRoute(viewId: string, params: Record<string, string> = {}, query: Record<string, string> = {}): string {
    const view = this.views.get(viewId);
    if (!view) throw new Error('Unknown app screen.');
    const parts = (view.route || view.id).split('/').flatMap(part => {
      if (!part.startsWith(':')) return [part];
      const optional = part.endsWith('?');
      const key = part.slice(1, optional ? -1 : undefined);
      const value = params[key];
      if (value === undefined || value === '') {
        if (optional) return [];
        throw new Error(`Missing route parameter: ${key}`);
      }
      return [encodeURIComponent(value)];
    });
    const search = new URLSearchParams();
    for (const key of Object.keys(query).sort()) search.set(key, query[key]);
    return parts.join('/') + (search.size ? `?${search}` : '');
  }

  /** Parse encoded parameters and the hash's query before matching a view. */
  public findByRoute(route: string): { view: IViewDefinition; params: Record<string, string>; query: Record<string, string> } | undefined {
    const separator = route.indexOf('?');
    const path = separator < 0 ? route : route.slice(0, separator);
    let parts: string[];
    try { parts = path.split('/').map(part => decodeURIComponent(part)); }
    catch { return undefined; }
    const query = Object.fromEntries(new URLSearchParams(separator < 0 ? '' : route.slice(separator + 1)));
    for (const view of this.views.values()) {
      const pattern = (view.route || view.id).split('/');
      const optional = pattern.at(-1)?.startsWith(':') && pattern.at(-1)?.endsWith('?');
      if (parts.length > pattern.length || parts.length < pattern.length - (optional ? 1 : 0)) continue;
      const params: Record<string, string> = {};
      const matches = pattern.every((part, index) => {
        if (!part.startsWith(':')) return part === parts[index];
        const isOptional = part.endsWith('?');
        if (parts[index] === undefined) return isOptional;
        if (!parts[index]) return false;
        params[part.slice(1, isOptional ? -1 : undefined)] = parts[index];
        return true;
      });
      if (matches && (view.validateRoute?.({ viewId: view.id, params, query }) ?? true)) return { view, params, query };
    }
    return undefined;
  }

  /**
   * Check if navigation away from current view is allowed
   */
  public async canLeaveCurrentView(destination?: IViewActivationContext): Promise<boolean | string> {
    if (!this.currentViewId) return true;

    const instance = this.instances.get(this.currentViewId);
    if (!instance) return true;

    const lifecycle = instance as unknown as IViewLifecycle;
    if (typeof lifecycle.canDeactivate === 'function') {
      return await lifecycle.canDeactivate(destination);
    }

    return true;
  }

  /**
   * Activate a view - handles caching, lifecycle, and rendering
   */
  public async activateView(
    viewId: string,
    container: HTMLElement,
    params?: Record<string, string>,
    query?: Record<string, string>
  ): Promise<HTMLElement | null> {
    const view = this.views.get(viewId);
    if (!view) {
      console.error(`View "${viewId}" not found in registry`);
      return null;
    }

    // Check if caching is enabled for this view (default: true)
    const shouldCache = view.cache !== false;

    const previousId = this.currentViewId;
    const previousContext = this.currentContext;
    let previousDeactivationStarted = false;
    let element = shouldCache ? this.instances.get(viewId) : undefined;
    const created = !element;
    if (!element) {
      const candidate = await this.createViewElement(view);
      if (!candidate || !container.isConnected) return null;
      element = candidate;
      element.style.display = 'none';
      container.appendChild(element);
    }
    try {
      await this.callOnActivate(element, viewId, params, query);
      if (!container.isConnected) throw new Error('View disconnected during activation.');
      if (previousId && previousId !== viewId) {
        previousDeactivationStarted = true;
        await this.deactivateView(previousId);
      }
      element.style.display = '';
      if (shouldCache) this.instances.set(viewId, element);
      this.currentViewId = viewId;
      this.currentContext = { params: { ...params }, query: { ...query } };
    } catch (error) {
      const failures = [error];
      // Cached targets also acquired activation resources; every failed activation needs cleanup.
      try { await (element as IViewLifecycle).onDeactivate?.(); } catch (cleanupError) { failures.push(cleanupError); }
      if (created) element.remove();
      const previous = previousId ? this.instances.get(previousId) : undefined;
      if (container.isConnected && previous && (previousId === viewId || previousDeactivationStarted)) {
        try {
          await this.callOnActivate(previous, previousId!, previousContext?.params, previousContext?.query);
          previous.style.display = '';
        } catch (restoreError) { failures.push(restoreError); }
      }
      if (failures.length > 1) throw new AggregateError(failures, 'View activation and lifecycle recovery failed.');
      throw error;
    }

    return element;
  }

  /**
   * Deactivate a view (hide and call lifecycle hook)
   */
  private async deactivateView(viewId: string): Promise<void> {
    const instance = this.instances.get(viewId);
    if (!instance) return;

    // Call onDeactivate lifecycle hook
    const lifecycle = instance as unknown as IViewLifecycle;
    if (typeof lifecycle.onDeactivate === 'function') {
      await lifecycle.onDeactivate();
    }

    // Hide the element
    instance.style.display = 'none';
  }

  /**
   * Create a view element from its definition (supports async content)
   */
  private async createViewElement(view: IViewDefinition): Promise<HTMLElement | null> {
    let content = view.content;

    // Handle async content (lazy loading)
    if (typeof content === 'function' &&
        !(content.prototype instanceof HTMLElement) &&
        content.constructor.name === 'AsyncFunction') {
      try {
        content = await (content as () => Promise<string | (new () => HTMLElement) | (() => TemplateResult)>)();
      } catch (error) {
        console.error(`Failed to load async content for view "${view.id}":`, error);
        return null;
      }
    }

    let element: HTMLElement;

    if (typeof content === 'string') {
      // Tag name string
      element = document.createElement(content);
    } else if (typeof content === 'function') {
      // Check if it's a class constructor or template function
      if (content.prototype instanceof HTMLElement) {
        // Element class constructor
        element = new (content as new () => HTMLElement)();
      } else {
        // Template function - wrap in a container and use Lit's render
        const wrapper = document.createElement('div');
        wrapper.className = 'view-content-wrapper';
        wrapper.style.cssText = 'display: contents;';
        const template = (content as () => TemplateResult)();
        render(template, wrapper);
        element = wrapper;
      }
    } else {
      console.error(`Invalid content type for view "${view.id}"`);
      return null;
    }

    // Add view ID as data attribute for debugging
    element.dataset.viewId = view.id;

    return element;
  }

  /**
   * Call onActivate lifecycle hook on a view element
   */
  private async callOnActivate(
    element: HTMLElement,
    viewId: string,
    params?: Record<string, string>,
    query?: Record<string, string>
  ): Promise<void> {
    const lifecycle = element as unknown as IViewLifecycle;
    if (typeof lifecycle.onActivate === 'function') {
      const context: IViewActivationContext = {
        appui: this.appui!,
        viewId,
        params,
        query,
      };
      await lifecycle.onActivate(context);
    }
  }

  /**
   * Legacy method - renders view without caching
   * @deprecated Use activateView instead
   */
  public renderView(viewId: string, container: HTMLElement): HTMLElement | null {
    const view = this.views.get(viewId);
    if (!view) {
      console.error(`View "${viewId}" not found in registry`);
      return null;
    }

    // For legacy compatibility, clear container
    container.innerHTML = '';

    let element: HTMLElement;
    const content = view.content;

    if (typeof content === 'string') {
      element = document.createElement(content);
    } else if (typeof content === 'function') {
      if ((content as any).prototype instanceof HTMLElement) {
        element = new (content as new () => HTMLElement)();
      } else {
        const wrapper = document.createElement('div');
        wrapper.className = 'view-content-wrapper';
        wrapper.style.cssText = 'display: contents;';
        const template = (content as () => TemplateResult)();
        render(template, wrapper);
        element = wrapper;
      }
    } else {
      console.error(`Invalid content type for view "${viewId}"`);
      return null;
    }

    container.appendChild(element);
    this.instances.set(viewId, element);
    this.currentViewId = viewId;

    return element;
  }

  /**
   * Get currently active view ID
   */
  public getCurrentViewId(): string | null {
    return this.currentViewId;
  }

  /**
   * Get cached instance of a view
   */
  public getInstance(viewId: string): HTMLElement | undefined {
    return this.instances.get(viewId);
  }

  /**
   * Clear a specific cached instance
   */
  public clearInstance(viewId: string): void {
    const instance = this.instances.get(viewId);
    if (instance && instance.parentNode) {
      instance.parentNode.removeChild(instance);
    }
    this.instances.delete(viewId);
    if (this.currentViewId === viewId) {
      this.currentViewId = null;
      this.currentContext = undefined;
    }
  }

  /**
   * Clear all instances
   */
  public clearInstances(): void {
    for (const [viewId, instance] of this.instances) {
      if (instance.parentNode) {
        instance.parentNode.removeChild(instance);
      }
    }
    this.instances.clear();
    this.currentViewId = null;
    this.currentContext = undefined;
  }

  /**
   * Unregister a view
   */
  public unregister(viewId: string): boolean {
    this.clearInstance(viewId);
    return this.views.delete(viewId);
  }

  /**
   * Clear the registry
   */
  public clear(): void {
    this.views.clear();
    this.clearInstances();
  }

  /**
   * Check if a view is registered
   */
  public has(viewId: string): boolean {
    return this.views.has(viewId);
  }

  /**
   * Get the number of registered views
   */
  public get size(): number {
    return this.views.size;
  }
}
